import { createContext, useState, useContext } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthContext } from './authContext'
import { fontSize, size } from '@styles/theme'
import styled from '@emotion/styled'
import { Button, Popover } from '@mui/material'
import { PiArrowRight } from 'react-icons/pi'

const PopoverContents = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${size(1.5)};
  gap: ${size(1)};
  border-radius: 15px;
`

const RegisterText = styled.span`
  font-size: ${fontSize('xs')};
`

const RegisterButton = styled(Button)`
  font-size: ${fontSize('xs')};
  border-radius: 100px;
  width: 100%;
`

const GlobalPopoverContext = createContext();
const GlobalPopoverProvider = ({ children }) => {
  const { state: authState } = useAuthContext()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [registerAnchorEl, setRegisterAnchorEl] = useState(null);
  const registerIsOpen = !!registerAnchorEl
  const id = registerIsOpen ? 'simple-popover' : undefined;

  const handleRegisterClose = () => setRegisterAnchorEl(null);
  const handleRegisterClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setRegisterAnchorEl(e.currentTarget)
  }

  const registerPopoverWrapper = (func) => {
    if (authState?.user?.isAnonymous) {
      return handleRegisterClick
    } else {
      return func
    }
  }

  const handleSignUpClick = () => {
    // WARNING: We have to use the search params directly here because the popover needs to be higher in the component tree
    //          than the modal context, which means we can't import the register modal directly (facepalm).
    //          So, setting the search params will work here, even if it's a bit hacky
    searchParams.append('action_modal', 'register-user')
    navigate({ search: searchParams.toString() })
    handleRegisterClose()
  }

  return (
    <GlobalPopoverContext.Provider value={{
      registerPopover: {
        anchorEl: registerAnchorEl,
        openOnClick: handleRegisterClick,
        close: handleRegisterClose,
        isOpen: !!registerAnchorEl,
        wrapper: registerPopoverWrapper,
      }
    }}>
      <Popover
        id={id}
        open={registerIsOpen}
        anchorEl={registerAnchorEl}
        onClose={handleRegisterClose}
        slotProps={{ paper: { style: {
          borderRadius: '10px',
        }}}}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}>
        <PopoverContents>
          <RegisterText>Register to interact with posts.</RegisterText>
          <RegisterButton
            onClick={handleSignUpClick}
            variant='contained'>
            Sign up here&nbsp;<PiArrowRight />
          </RegisterButton>
        </PopoverContents>
      </Popover>
      {children}
    </GlobalPopoverContext.Provider>
  )
}

const useGlobalPopoverContext = () => useContext(GlobalPopoverContext)

export default useGlobalPopoverContext

export { GlobalPopoverContext, GlobalPopoverProvider, useGlobalPopoverContext }
